/**
 * IT > Adoption at /it/adoption.
 *
 * How much of what we built is actually being used: five pillar tiles
 * (Help desk, Store tickets, Cockpit, Store app, AI assistant) each showing a
 * percent of the population that used it, a 12-week trend, and a status pill,
 * over a grid of every tool with its own usage against the people or stores
 * who hold it. Clicking a usage number drills into who used it, and clicking
 * a person or store swaps to their own cross-tool usage.
 *
 * Backend: /v1/adoption/* on the shared API, built in parallel in the api
 * repo against this exact contract (see the task brief). Every read below
 * degrades to a quiet "not available yet" state on 404/503 rather than
 * crashing (COCKPIT.md 3, the Connect / Item Sales pattern), because the
 * backend may not be deployed yet when this page ships.
 *
 * Deviations from the contract, recorded here rather than left implicit:
 * - The Export button is omitted. The contract has no export endpoint for
 *   this tool, and COCKPIT.md's rule for an unbuilt feature is to hide it
 *   with a note rather than ship a button that 404s.
 * - A pillar's usage number drills into the SAME tool_key as the pillar's
 *   own `key` (i.e. each pillar maps to exactly one tool). The contract
 *   does not name the tool_key on the pillar object explicitly; this is
 *   the natural reading of "the N is a button that opens the tool
 *   drill-down where the pillar maps to a single tool."
 * - The tools grid's Status filter sends `status=used|never_used|awaiting_launch`
 *   to the api; the contract names the query param but not its enum values.
 * - Sorting on the tools grid is client-side (the contract's /tools endpoint
 *   takes no sort param), persisted to the URL for a shareable link.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  Check,
  ChevronRight,
  Download,
  Loader2,
  Mail,
  MailCheck,
  Settings2,
  X,
} from 'lucide-react';

import { Pill } from '@/components/Pill';
import { FilterLabel } from '@/components/FilterLabel';
import { Modal } from '@/components/Modal';
import { ToolPinToggle } from '@/components/ToolPinToggle';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { DataGridShell } from '@/components/grid/DataGridShell';
import { gridTheadClassName, SortableHeader, type SortDir } from '@ampersandbrands/ui';
import { formatDate, formatDateMaybeTime } from '@/lib/dateUtils';
import {
  useCreateReportSubscription,
  useDeleteReportSubscription,
  useReportSubscriptions,
} from '@/lib/useReportSubscriptions';
import {
  ADOPTION_RANGE_OPTIONS,
  ADOPTION_STATUS_OPTIONS,
  ADOPTION_SURFACES,
  DEFAULT_ADOPTION_RANGE,
  isAwaitingLaunch,
  isNotAvailable,
  isValidRangeKey,
  pillarStatus,
  rangeKeyToDays,
  safeArray,
  SURFACE_FILTER_LABELS,
  downloadAdoptionXlsx,
  surfaceLabel,
  useAdoptionSettings,
  useAdoptionSummary,
  useAdoptionTools,
  useAdoptionToolUsers,
  useAdoptionUserDetail,
  useUpdateAdoptionSettings,
  type AdoptionPillar,
  type AdoptionRangeKey,
  type AdoptionSettingsResponse,
  type AdoptionStatusFilter,
  type AdoptionSurface,
  type AdoptionTool,
  type PillarStatusTone,
} from '@/lib/useAdoption';

const ADOPTION_REPORT_TYPE = 'adoption';

const TONE_PILL_CLASS: Record<PillarStatusTone, string> = {
  good: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
  bad: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  warn: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
};

type ToolSortKey = 'label' | 'section' | 'surface' | 'expected_from' | 'used' | 'access' | 'pct' | 'last_used_at';

const ROLLING_WINDOW_NOTE = 'The window ends yesterday; today is still in progress.';

// ---------------------------------------------------------------------------
// Small building blocks
// ---------------------------------------------------------------------------

function Sparkline({ series, tone }: { series: Array<{ week_start: string; used: number }>; tone: PillarStatusTone | 'neutral' }) {
  if (series.length === 0) {
    return <div className="h-8 w-full" />;
  }
  const max = Math.max(1, ...series.map((p) => p.used));
  const barW = 6;
  const gap = 2;
  const barFill =
    tone === 'good'
      ? 'fill-green-500 dark:fill-green-400'
      : tone === 'bad'
        ? 'fill-red-500 dark:fill-red-400'
        : tone === 'warn'
          ? 'fill-amber-500 dark:fill-amber-400'
          : 'fill-amp-olive dark:fill-amp-olive-dark';
  return (
    <svg
      viewBox={`0 0 ${series.length * (barW + gap)} 32`}
      className="h-8 w-full"
      preserveAspectRatio="none"
      aria-hidden
    >
      {series.map((p, i) => {
        const h = Math.max(2, Math.round((p.used / max) * 30));
        return (
          <rect
            key={p.week_start}
            x={i * (barW + gap)}
            y={32 - h}
            width={barW}
            height={h}
            rx={1}
            className={barFill}
          />
        );
      })}
    </svg>
  );
}

function PillarTile({
  pillar,
  historyFrom,
  onDrill,
}: {
  pillar: AdoptionPillar;
  historyFrom: string | null;
  onDrill: (toolKey: string, label: string) => void;
}) {
  const status = pillarStatus(pillar, historyFrom);
  const pctDisplay = pillar.pct === null || pillar.pct === undefined ? '--' : `${Math.round(pillar.pct)}%`;
  const canDrill = pillar.used !== null && pillar.used !== undefined && pillar.used > 0;

  return (
    <div className="min-w-[180px] flex-1 basis-[180px] rounded-lg border border-amp-line bg-amp-white p-4 dark:border-amp-dark-line dark:bg-amp-dark-surface">
      <p className="mb-1 text-xs uppercase tracking-wide text-amp-ink-muted dark:text-amp-dark-ink-muted">
        {pillar.label}
      </p>
      <p className="mb-1 text-3xl font-bold text-amp-ink dark:text-amp-dark-ink">{pctDisplay}</p>
      {canDrill ? (
        <button
          type="button"
          onClick={() => onDrill(pillar.key, pillar.label)}
          className="text-sm text-amp-ink-muted underline hover:text-amp-ink dark:text-amp-dark-ink-muted dark:hover:text-amp-dark-ink"
        >
          {pillar.used} of {pillar.population ?? '--'}
        </button>
      ) : (
        <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
          {pillar.used ?? '--'} of {pillar.population ?? '--'}
        </p>
      )}
      <div className="my-2">
        <Sparkline series={pillar.series ?? []} tone={status.tone === 'warn' ? 'neutral' : status.tone} />
      </div>
      <Pill tone={TONE_PILL_CLASS[status.tone]}>{status.label}</Pill>
    </div>
  );
}

function ProgressCell({ pct }: { pct: number | null }) {
  const v = pct === null || pct === undefined ? 0 : Math.max(0, Math.min(100, pct));
  return (
    <div className="flex items-center gap-2">
      <div className="h-1.5 w-16 shrink-0 overflow-hidden rounded-full bg-amp-cream-warm dark:bg-amp-dark-surface-warm">
        <div className="h-full rounded-full bg-amp-olive dark:bg-amp-olive-dark" style={{ width: `${v}%` }} />
      </div>
      <span className="text-[12px] text-amp-ink-muted dark:text-amp-dark-ink-muted">
        {pct === null || pct === undefined ? '--' : `${Math.round(pct)}%`}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Subscribe strip (Item Sales pattern, report_type 'adoption')
// ---------------------------------------------------------------------------

const SUBSCRIBE_STRIP_CLASS =
  'shrink-0 mb-4 flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-amp-line bg-amp-white px-3 py-2 dark:border-amp-dark-line dark:bg-amp-dark-surface';

const ADOPTION_ROLLING_NOTE =
  'Your filters are saved with the subscription. The date range is not: each email works its window out fresh on the morning it sends.';

function subscriptionFilters(surface: AdoptionSurface | 'all', status: AdoptionStatusFilter, includeIt: boolean) {
  const out: Record<string, unknown> = {};
  if (surface !== 'all') out.surface = surface;
  if (status !== 'all') out.status = status;
  if (includeIt) out.include_it = true;
  return out;
}

function filtersKey(filters: Record<string, unknown> | null | undefined): string {
  if (!filters) return '';
  return Object.keys(filters)
    .sort()
    .map((k) => `${k}=${String(filters[k])}`)
    .join('&');
}

function AdoptionSubscribeControl({
  surface,
  status,
  includeIt,
}: {
  surface: AdoptionSurface | 'all';
  status: AdoptionStatusFilter;
  includeIt: boolean;
}) {
  const { subscriptions, available } = useReportSubscriptions();
  const createMut = useCreateReportSubscription();
  const deleteMut = useDeleteReportSubscription();
  const [feedback, setFeedback] = useState<{ kind: 'saved' | 'error'; msg: string } | null>(null);

  useEffect(() => {
    if (!feedback) return;
    const t = window.setTimeout(() => setFeedback(null), 4000);
    return () => window.clearTimeout(t);
  }, [feedback]);

  const savedFilters = useMemo(() => subscriptionFilters(surface, status, includeIt), [surface, status, includeIt]);
  const existing = useMemo(() => {
    const key = filtersKey(savedFilters);
    return (
      subscriptions.find(
        (s) => s.report_type === ADOPTION_REPORT_TYPE && s.status === 'active' && filtersKey(s.filters) === key,
      ) ?? null
    );
  }, [subscriptions, savedFilters]);

  if (!available) {
    return (
      <div className={SUBSCRIBE_STRIP_CLASS}>
        <span className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
          The daily email for this report is unavailable right now, so you cannot subscribe.
        </span>
        <div className="ml-auto">
          <button
            type="button"
            disabled
            className="inline-flex cursor-not-allowed items-center gap-1 rounded border border-amp-line px-3 py-1.5 text-xs text-amp-ink opacity-60 dark:border-amp-dark-line dark:text-amp-dark-ink"
          >
            <Mail className="h-3 w-3" aria-hidden />
            Subscribe
          </button>
        </div>
      </div>
    );
  }

  const isBusy = createMut.isPending || deleteMut.isPending;
  const subscribed = existing !== null;

  const handleClick = () => {
    if (isBusy) return;
    if (existing) {
      deleteMut.mutate(existing.id, {
        onSuccess: () => setFeedback({ kind: 'saved', msg: 'Unsubscribed' }),
        onError: (e) => setFeedback({ kind: 'error', msg: e instanceof Error ? e.message : 'Could not unsubscribe' }),
      });
      return;
    }
    createMut.mutate(
      { reportType: ADOPTION_REPORT_TYPE, filters: savedFilters },
      {
        onSuccess: () => setFeedback({ kind: 'saved', msg: 'Subscribed. Manage it in Settings.' }),
        onError: (e) => setFeedback({ kind: 'error', msg: e instanceof Error ? e.message : 'Could not subscribe' }),
      },
    );
  };

  const Icon = isBusy ? Loader2 : subscribed ? MailCheck : Mail;
  const buttonLabel = isBusy ? (subscribed ? 'Unsubscribing...' : 'Subscribing...') : subscribed ? 'Unsubscribe' : 'Subscribe';

  return (
    <div className={SUBSCRIBE_STRIP_CLASS}>
      <div className="min-w-[280px] flex-1">
        <p className="line-clamp-2 text-sm text-amp-ink md:line-clamp-none dark:text-amp-dark-ink">
          {subscribed
            ? 'You get this view by email every Monday, with the filters saved on this subscription.'
            : 'Email me this view every Monday, with the surface and status filters you have applied here.'}
        </p>
        <p className="hidden text-[12px] text-amp-ink-muted md:block dark:text-amp-dark-ink-muted">
          {ADOPTION_ROLLING_NOTE}
        </p>
      </div>
      <div className="ml-auto flex items-center gap-2">
        {feedback && (
          <span
            role="status"
            className={
              feedback.kind === 'error'
                ? 'text-[12px] font-semibold text-red-700 dark:text-red-300'
                : 'inline-flex items-center gap-1 text-[12px] font-semibold text-amp-ink dark:text-amp-dark-ink'
            }
          >
            {feedback.kind === 'saved' && <Check className="h-3 w-3" aria-hidden />}
            {feedback.msg}
          </span>
        )}
        <Link
          to="/settings"
          className="text-[12px] text-amp-ink-muted underline hover:text-amp-ink hover:no-underline dark:text-amp-dark-ink-muted dark:hover:text-amp-dark-ink"
        >
          Manage
        </Link>
        <button
          type="button"
          onClick={handleClick}
          disabled={isBusy}
          className="inline-flex items-center gap-1 rounded border border-amp-line px-3 py-1.5 text-xs text-amp-ink hover:bg-amp-cream-warm disabled:cursor-not-allowed disabled:opacity-60 dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm"
        >
          <Icon className={`h-3 w-3 ${isBusy ? 'animate-spin' : ''}`} aria-hidden />
          {buttonLabel}
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Drill-down modal: tool -> users -> one user
// ---------------------------------------------------------------------------

type DrillState = { toolKey: string; toolLabel: string; principalRef: string | null };

function DrillDownModal({
  drill,
  days,
  includeIt,
  onSelectUser,
  onBackToList,
  onClose,
}: {
  drill: DrillState;
  days: ReturnType<typeof rangeKeyToDays>;
  includeIt: boolean;
  onSelectUser: (ref: string) => void;
  onBackToList: () => void;
  onClose: () => void;
}) {
  const usersQ = useAdoptionToolUsers(drill.principalRef ? null : drill.toolKey, days, includeIt);
  const userQ = useAdoptionUserDetail(drill.principalRef, days);

  if (drill.principalRef) {
    return (
      <Modal title={drill.toolLabel} eyebrow="Adoption" onClose={onClose}>
        <div className="max-h-[70vh] overflow-y-auto px-5 py-4">
          <button
            type="button"
            onClick={onBackToList}
            className="mb-3 inline-flex items-center gap-1 text-sm text-amp-ink-muted hover:text-amp-ink dark:text-amp-dark-ink-muted dark:hover:text-amp-dark-ink"
          >
            <ArrowLeft className="h-3 w-3" aria-hidden /> Back to who used it
          </button>
          {userQ.isLoading ? (
            <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">Loading...</p>
          ) : userQ.error ? (
            isNotAvailable(userQ.error) ? (
              <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">Not available yet.</p>
            ) : (
              <p className="text-sm text-red-700 dark:text-red-300">Could not load: {userQ.error.message}</p>
            )
          ) : (
            <>
              <p className="mb-1 text-base font-semibold text-amp-ink dark:text-amp-dark-ink">
                {userQ.data?.user.name ?? drill.principalRef}
                {userQ.data?.user.is_it && (
                  <span className="ml-2 align-middle">
                    <Pill tone="bg-amp-cream-warm text-amp-ink dark:bg-amp-dark-surface-warm dark:text-amp-dark-ink">IT</Pill>
                  </span>
                )}
              </p>
              <p className="mb-3 text-xs uppercase tracking-wide text-amp-ink-muted dark:text-amp-dark-ink-muted">
                {userQ.data?.user.kind ?? 'person'}
              </p>
              <table className="min-w-full text-sm">
                <thead className={gridTheadClassName}>
                  <tr>
                    <th className="px-2 py-1.5 text-left font-medium">Tool</th>
                    <th className="px-2 py-1.5 text-left font-medium">Surface</th>
                    <th className="px-2 py-1.5 text-left font-medium">Last used</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-amp-line dark:divide-amp-dark-line">
                  {safeArray<{ tool_key: string; label: string; surface: string; last_used_at: string | null }>(
                    userQ.data?.tools,
                  ).map((t) => (
                    <tr key={t.tool_key}>
                      <td className="px-2 py-1.5">{t.label}</td>
                      <td className="px-2 py-1.5">{surfaceLabel(t.surface)}</td>
                      <td className="px-2 py-1.5">{formatDateMaybeTime(t.last_used_at)}</td>
                    </tr>
                  ))}
                  {(userQ.data?.tools?.length ?? 0) === 0 && (
                    <tr>
                      <td colSpan={3} className="px-2 py-3 text-amp-ink-muted dark:text-amp-dark-ink-muted">
                        No tool usage in this window.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </>
          )}
        </div>
      </Modal>
    );
  }

  return (
    <Modal title={`Who used ${drill.toolLabel}`} eyebrow="Adoption" onClose={onClose}>
      <div className="max-h-[70vh] overflow-y-auto px-5 py-4">
        {usersQ.isLoading ? (
          <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">Loading...</p>
        ) : usersQ.error ? (
          isNotAvailable(usersQ.error) ? (
            <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">Not available yet.</p>
          ) : (
            <p className="text-sm text-red-700 dark:text-red-300">Could not load: {usersQ.error.message}</p>
          )
        ) : (
          <ul className="divide-y divide-amp-line dark:divide-amp-dark-line">
            {safeArray<{
              principal_ref: string;
              name: string;
              kind: string;
              is_it: boolean;
              tools_used: number;
              last_used_at: string | null;
            }>(usersQ.data?.users).map((u) => (
              <li key={u.principal_ref}>
                <button
                  type="button"
                  onClick={() => onSelectUser(u.principal_ref)}
                  className="flex w-full items-center gap-3 py-2 text-left hover:bg-amp-cream-warm dark:hover:bg-amp-dark-surface-warm"
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-amp-ink dark:text-amp-dark-ink">
                      {u.name ?? u.principal_ref}
                      {u.is_it && (
                        <span className="ml-2 align-middle">
                          <Pill tone="bg-amp-cream-warm text-amp-ink dark:bg-amp-dark-surface-warm dark:text-amp-dark-ink">
                            IT
                          </Pill>
                        </span>
                      )}
                    </p>
                    <p className="truncate text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
                      {u.kind} · {u.tools_used} tool{u.tools_used === 1 ? '' : 's'} · last used{' '}
                      {formatDateMaybeTime(u.last_used_at)}
                    </p>
                  </div>
                  <ChevronRight className="h-4 w-4 shrink-0 text-amp-ink-muted dark:text-amp-dark-ink-muted" aria-hidden />
                </button>
              </li>
            ))}
            {(usersQ.data?.users?.length ?? 0) === 0 && (
              <li className="py-3 text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
                Nobody used this tool in this window.
              </li>
            )}
          </ul>
        )}
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Settings modal: targets, per-tool launch dates, IT-member list
// ---------------------------------------------------------------------------

function SettingsModal({ onClose }: { onClose: () => void }) {
  const settingsQ = useAdoptionSettings(true);
  const updateMut = useUpdateAdoptionSettings();

  const [targets, setTargets] = useState<AdoptionSettingsResponse['targets']>([]);
  const [tools, setTools] = useState<AdoptionSettingsResponse['tools']>([]);
  const [members, setMembers] = useState<string[]>([]);
  const [origMembers, setOrigMembers] = useState<string[]>([]);
  const [newMember, setNewMember] = useState('');
  const [toolFilter, setToolFilter] = useState('');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!settingsQ.data) return;
    setTargets(settingsQ.data.targets ?? []);
    setTools(settingsQ.data.tools ?? []);
    const emails = (settingsQ.data.it_members ?? []).map((m) => m.email);
    setMembers(emails);
    setOrigMembers(emails);
  }, [settingsQ.data]);

  const visibleTools = toolFilter.trim()
    ? tools.filter(
        (t) =>
          t.label.toLowerCase().includes(toolFilter.trim().toLowerCase()) ||
          t.tool_key.toLowerCase().includes(toolFilter.trim().toLowerCase()) ||
          (t.section ?? '').toLowerCase().includes(toolFilter.trim().toLowerCase()),
      )
    : tools;

  const handleSave = () => {
    setSaveError(null);
    // An email typed into the add box but not yet Add-ed still counts:
    // silently dropping it read as "Save does nothing" (Steven, 2026-08-27).
    const pending = newMember.trim().toLowerCase();
    const allMembers = pending && !members.includes(pending) ? [...members, pending] : members;
    const today = new Date().toISOString().slice(0, 10);
    // The api schema takes {email, effective_from} per added member; the
    // original bare-string shape failed validation on every save that added
    // someone, which was the real "not saving" bug.
    const add = allMembers.filter((m) => !origMembers.includes(m)).map((email) => ({ email, effective_from: today }));
    const remove = origMembers.filter((m) => !allMembers.includes(m));
    updateMut.mutate(
      {
        targets: targets.map((t) => ({ line_key: t.line_key, target: t.target, expected_from: t.expected_from })),
        tools: tools.map((t) => ({ tool_key: t.tool_key, expected_from: t.expected_from })),
        it_members: add.length || remove.length ? { add, remove } : undefined,
      },
      {
        onSuccess: () => {
          setNewMember('');
          setSaved(true);
          window.setTimeout(() => onClose(), 900);
        },
        onError: (e) => setSaveError(e instanceof Error ? e.message : 'Could not save'),
      },
    );
  };

  const unavailable = !!settingsQ.error && isNotAvailable(settingsQ.error);

  return (
    <Modal title="Targets and launch dates" eyebrow="Adoption" onClose={onClose} widthClassName="max-w-3xl">
      <div className="max-h-[75vh] overflow-y-auto px-5 py-4">
        {settingsQ.isLoading ? (
          <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">Loading...</p>
        ) : unavailable ? (
          <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
            Settings are not available yet. The tiles and grid still work off the api's default targets.
          </p>
        ) : settingsQ.error ? (
          <p className="text-sm text-red-700 dark:text-red-300">Could not load settings: {settingsQ.error.message}</p>
        ) : (
          <>
            <section className="mb-6">
              <h3 className="mb-2 text-sm font-semibold text-amp-ink dark:text-amp-dark-ink">Scorecard targets</h3>
              <div className="space-y-2">
                {targets.map((t, i) => (
                  <div key={t.line_key} className="flex flex-wrap items-center gap-2 rounded border border-amp-line px-2 py-1.5 dark:border-amp-dark-line">
                    <span className="min-w-[140px] flex-1 text-sm text-amp-ink dark:text-amp-dark-ink">{t.label}</span>
                    <label className="flex items-center gap-1 text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
                      Target
                      <input
                        type="number"
                        value={t.target}
                        onChange={(e) => {
                          const v = Number(e.target.value);
                          setTargets((prev) => prev.map((x, idx) => (idx === i ? { ...x, target: v } : x)));
                        }}
                        className="w-20 rounded border border-amp-line bg-amp-white px-2 py-1 text-sm dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
                      />
                      %
                    </label>
                    <label className="flex items-center gap-1 text-xs text-amp-ink-muted dark:text-amp-dark-ink-muted">
                      Launches
                      <input
                        type="date"
                        value={t.expected_from ?? ''}
                        onChange={(e) => {
                          const v = e.target.value || null;
                          setTargets((prev) => prev.map((x, idx) => (idx === i ? { ...x, expected_from: v } : x)));
                        }}
                        className="rounded border border-amp-line bg-amp-white px-2 py-1 text-sm dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
                      />
                    </label>
                  </div>
                ))}
                {targets.length === 0 && (
                  <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">No scorecard lines yet.</p>
                )}
              </div>
            </section>

            <section className="mb-6">
              <h3 className="mb-2 text-sm font-semibold text-amp-ink dark:text-amp-dark-ink">Per-tool launch dates</h3>
              {tools.length > 15 && (
                <input
                  type="text"
                  value={toolFilter}
                  onChange={(e) => setToolFilter(e.target.value)}
                  placeholder="Filter tools..."
                  className="mb-2 w-full rounded border border-amp-line bg-amp-white px-2 py-1.5 text-sm dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
                />
              )}
              <div className="max-h-64 space-y-1 overflow-y-auto">
                {visibleTools.map((t) => (
                  <div key={t.tool_key} className="flex items-center gap-2 rounded border border-amp-line px-2 py-1.5 dark:border-amp-dark-line">
                    <span className="min-w-[160px] flex-1 truncate text-sm text-amp-ink dark:text-amp-dark-ink">
                      {t.surface ? (
                        <span className="text-amp-ink-muted dark:text-amp-dark-ink-muted">
                          {surfaceLabel(t.surface)} / {t.section ? `${t.section} / ` : ''}
                        </span>
                      ) : null}
                      {t.label}
                    </span>
                    <input
                      type="date"
                      value={t.expected_from ?? ''}
                      onChange={(e) => {
                        const v = e.target.value || null;
                        setTools((prev) => prev.map((x) => (x.tool_key === t.tool_key ? { ...x, expected_from: v } : x)));
                      }}
                      className="rounded border border-amp-line bg-amp-white px-2 py-1 text-sm dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
                    />
                  </div>
                ))}
                {visibleTools.length === 0 && (
                  <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">No tools match.</p>
                )}
              </div>
            </section>

            <section className="mb-2">
              <h3 className="mb-2 text-sm font-semibold text-amp-ink dark:text-amp-dark-ink">
                Who counts as IT (excluded unless "Include IT" is checked)
              </h3>
              <div className="mb-2 flex gap-2">
                <input
                  type="email"
                  value={newMember}
                  onChange={(e) => setNewMember(e.target.value)}
                  placeholder="name@ampersandbrands.com"
                  className="flex-1 rounded border border-amp-line bg-amp-white px-2 py-1.5 text-sm dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
                />
                <button
                  type="button"
                  onClick={() => {
                    const v = newMember.trim().toLowerCase();
                    if (v && !members.includes(v)) setMembers((prev) => [...prev, v]);
                    setNewMember('');
                  }}
                  className="rounded border border-amp-line px-3 py-1.5 text-xs text-amp-ink hover:bg-amp-cream-warm dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm"
                >
                  Add
                </button>
              </div>
              <ul className="space-y-1">
                {members.map((m) => (
                  <li key={m} className="flex items-center justify-between rounded border border-amp-line px-2 py-1 text-sm dark:border-amp-dark-line">
                    <span className="text-amp-ink dark:text-amp-dark-ink">{m}</span>
                    <button
                      type="button"
                      onClick={() => setMembers((prev) => prev.filter((x) => x !== m))}
                      className="text-amp-ink-muted hover:text-red-700 dark:text-amp-dark-ink-muted dark:hover:text-red-300"
                      aria-label={`Remove ${m}`}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </li>
                ))}
                {members.length === 0 && (
                  <li className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">Nobody flagged as IT yet.</li>
                )}
              </ul>
            </section>

            <p className="mt-4 text-[12px] text-amp-ink-muted dark:text-amp-dark-ink-muted">Every change is kept.</p>
            {saveError && <p className="mt-1 text-[12px] text-red-700 dark:text-red-300">{saveError}</p>}
          </>
        )}
      </div>
      <div className="flex items-center justify-end gap-2 border-t border-amp-line px-5 py-3 dark:border-amp-dark-line">
        {saveError && (
          <p className="mr-auto text-[12px] text-red-700 dark:text-red-300">Could not save: {saveError}</p>
        )}
        <button
          type="button"
          onClick={onClose}
          className="rounded border border-amp-line px-3 py-1.5 text-sm text-amp-ink hover:bg-amp-cream-warm dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          disabled={updateMut.isPending || saved || settingsQ.isLoading || unavailable}
          className="rounded bg-amp-olive-deep px-3 py-1.5 text-sm font-semibold text-amp-cream hover:bg-amp-olive disabled:cursor-not-allowed disabled:opacity-60"
        >
          {saved ? 'Saved' : updateMut.isPending ? 'Saving...' : 'Save'}
        </button>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

function AdoptionInner() {
  const [searchParams, setSearchParams] = useSearchParams();

  const rangeKey = isValidRangeKey(searchParams.get('range')) ? (searchParams.get('range') as AdoptionRangeKey) : DEFAULT_ADOPTION_RANGE;
  const days = rangeKeyToDays(rangeKey);
  const surfaceParam = searchParams.get('surface');
  const surface: AdoptionSurface | 'all' = (ADOPTION_SURFACES as string[]).includes(surfaceParam ?? '')
    ? (surfaceParam as AdoptionSurface)
    : 'all';
  const statusParam = searchParams.get('status') as AdoptionStatusFilter | null;
  const status: AdoptionStatusFilter = ADOPTION_STATUS_OPTIONS.some((o) => o.value === statusParam) ? (statusParam as AdoptionStatusFilter) : 'all';
  const includeIt = searchParams.get('include_it') === '1';
  const sortKey = (searchParams.get('sort') as ToolSortKey | null) ?? null;
  const sortDir = (searchParams.get('dir') as SortDir | null) ?? 'asc';

  const update = (mutate: (sp: URLSearchParams) => void) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        mutate(next);
        return next;
      },
      { replace: true },
    );
  };

  const [drill, setDrill] = useState<DrillState | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const summaryQ = useAdoptionSummary(days, includeIt);
  const toolsQ = useAdoptionTools({ days, includeIt, surface, status });

  const historyFrom = summaryQ.data?.history_from ?? null;
  const pillars = safeArray<AdoptionPillar>(summaryQ.data?.pillars);
  const allTools = safeArray<AdoptionTool>(toolsQ.data?.tools);

  const sortedTools = useMemo(() => {
    if (!sortKey) return allTools;
    const dir = sortDir === 'desc' ? -1 : 1;
    const withNulls = (v: unknown): [number, unknown] => (v === null || v === undefined ? [1, v] : [0, v]);
    return [...allTools].sort((a, b) => {
      const av = a[sortKey as keyof AdoptionTool] as unknown;
      const bv = b[sortKey as keyof AdoptionTool] as unknown;
      const [an, aval] = withNulls(av);
      const [bn, bval] = withNulls(bv);
      if (an !== bn) return an - bn;
      if (aval === bval) return 0;
      if (typeof aval === 'number' && typeof bval === 'number') return (aval - bval) * dir;
      return String(aval).localeCompare(String(bval)) * dir;
    });
  }, [allTools, sortKey, sortDir]);

  const launchedTools = sortedTools.filter((t) => !isAwaitingLaunch(t));
  const awaitingCount = sortedTools.length - launchedTools.length;

  const toggleSort = (key: ToolSortKey) =>
    update((sp) => {
      if (sortKey === key) {
        sp.set('dir', sortDir === 'asc' ? 'desc' : 'asc');
      } else {
        sp.set('sort', key);
        sp.set('dir', 'asc');
      }
    });
  const sortedFor = (key: ToolSortKey): SortDir | null => (sortKey === key ? sortDir : null);

  const activeFilterCount = [surface !== 'all', status !== 'all', includeIt].filter(Boolean).length;

  const notAvailable = toolsQ.error && isNotAvailable(toolsQ.error);
  const summaryNotAvailable = summaryQ.error && isNotAvailable(summaryQ.error);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <Link
        to="/"
        className="mb-3 inline-flex items-center gap-1 text-sm text-amp-ink-muted hover:text-amp-ink dark:text-amp-dark-ink-muted dark:hover:text-amp-dark-ink"
      >
        <ArrowLeft className="h-3 w-3" /> Back to dashboard
      </Link>

      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <p className="mb-1 text-xs uppercase tracking-wide text-amp-olive dark:text-amp-olive-dark">IT</p>
          <h1 className="text-2xl font-semibold text-amp-ink dark:text-amp-dark-ink">Adoption</h1>
          <p className="mt-1 max-w-2xl text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">
            How much of what we built is actually being used, per tool against the people or stores who hold it.
          </p>
        </div>
        <ToolPinToggle toolSlug="adoption" toolName="Adoption" />
      </div>

      <AdoptionSubscribeControl surface={surface} status={status} includeIt={includeIt} />

      {summaryNotAvailable ? (
        <div className="mb-6 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-900/40 dark:bg-amber-900/20 dark:text-amber-200">
          The pillar summary is not available yet.
        </div>
      ) : (
        <div className="mb-6 flex flex-wrap gap-3">
          {pillars.map((p) => (
            <PillarTile
              key={p.key}
              pillar={p}
              historyFrom={historyFrom}
              onDrill={(toolKey, label) => setDrill({ toolKey, toolLabel: label, principalRef: null })}
            />
          ))}
          {pillars.length === 0 && !summaryQ.isLoading && (
            <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">No pillar data for this window.</p>
          )}
        </div>
      )}

      {notAvailable ? (
        <div className="rounded-lg border border-amp-line bg-amp-white p-6 text-center dark:border-amp-dark-line dark:bg-amp-dark-surface">
          <p className="text-sm text-amp-ink-muted dark:text-amp-dark-ink-muted">Adoption data is not available yet.</p>
        </div>
      ) : (
        <>
          <p className="mb-2 shrink-0 text-[12px] text-amp-ink-muted dark:text-amp-dark-ink-muted">
            Showing {launchedTools.length} of {sortedTools.length} tools
            {awaitingCount > 0 && ` · ${awaitingCount} awaiting launch, kept out of the totals`}
            {` · ${ROLLING_WINDOW_NOTE}`}
            {historyFrom && sortedTools.length === 0 && ` · saved history starts ${formatDate(historyFrom)}`}
          </p>

          <DataGridShell
            toolbar={
              <div className="flex flex-wrap items-end gap-3">
                <div>
                  <FilterLabel as="div">Range</FilterLabel>
                  <select
                    value={rangeKey}
                    onChange={(e) => update((sp) => sp.set('range', e.target.value))}
                    className="rounded border border-amp-line bg-amp-white px-2 py-1.5 text-sm dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
                  >
                    {ADOPTION_RANGE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <FilterLabel as="div">Surface</FilterLabel>
                  <select
                    value={surface}
                    onChange={(e) => update((sp) => (e.target.value === 'all' ? sp.delete('surface') : sp.set('surface', e.target.value)))}
                    className="rounded border border-amp-line bg-amp-white px-2 py-1.5 text-sm dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
                  >
                    <option value="all">All</option>
                    {ADOPTION_SURFACES.map((s) => (
                      <option key={s} value={s}>
                        {SURFACE_FILTER_LABELS[s]}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <FilterLabel as="div">Status</FilterLabel>
                  <select
                    value={status}
                    onChange={(e) => update((sp) => (e.target.value === 'all' ? sp.delete('status') : sp.set('status', e.target.value)))}
                    className="rounded border border-amp-line bg-amp-white px-2 py-1.5 text-sm dark:border-amp-dark-line dark:bg-amp-dark-surface dark:text-amp-dark-ink"
                  >
                    {ADOPTION_STATUS_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
                <label className="flex items-center gap-1.5 pb-1.5 text-sm text-amp-ink dark:text-amp-dark-ink">
                  <input
                    type="checkbox"
                    checked={includeIt}
                    onChange={(e) => update((sp) => (e.target.checked ? sp.set('include_it', '1') : sp.delete('include_it')))}
                  />
                  Include IT
                </label>
                <button
                  type="button"
                  onClick={() => setSettingsOpen(true)}
                  className="ml-auto inline-flex items-center gap-1 rounded border border-amp-line px-3 py-1.5 text-xs text-amp-ink hover:bg-amp-cream-warm dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm"
                >
                  <Settings2 className="h-3.5 w-3.5" aria-hidden />
                  Targets and launch dates
                </button>
                <button
                  type="button"
                  disabled={exporting}
                  onClick={async () => {
                    setExporting(true);
                    setExportError(null);
                    try {
                      await downloadAdoptionXlsx({ days, includeIt, surface, status });
                    } catch (e) {
                      setExportError(e instanceof Error ? e.message : 'Export failed');
                    } finally {
                      setExporting(false);
                    }
                  }}
                  className="inline-flex items-center gap-1 rounded border border-amp-line px-3 py-1.5 text-xs text-amp-ink hover:bg-amp-cream-warm disabled:cursor-not-allowed disabled:opacity-60 dark:border-amp-dark-line dark:text-amp-dark-ink dark:hover:bg-amp-dark-surface-warm"
                >
                  <Download className="h-3.5 w-3.5" aria-hidden />
                  {exporting ? 'Exporting...' : 'Export'}
                </button>
                {exportError && (
                  <p className="w-full text-xs text-red-700 dark:text-red-300">{exportError}</p>
                )}
              </div>
            }
            activeFilterCount={activeFilterCount}
          >
            <table className="min-w-full text-sm">
              <thead className={gridTheadClassName}>
                <tr>
                  <SortableHeader label="Tool" sorted={sortedFor('label')} onToggle={() => toggleSort('label')} thClassName="px-3 py-2" />
                  <SortableHeader label="Section" sorted={sortedFor('section')} onToggle={() => toggleSort('section')} thClassName="px-3 py-2 w-[160px]" />
                  <SortableHeader label="Surface" sorted={sortedFor('surface')} onToggle={() => toggleSort('surface')} thClassName="px-3 py-2 w-[130px]" />
                  <SortableHeader
                    label="Launch date"
                    sorted={sortedFor('expected_from')}
                    onToggle={() => toggleSort('expected_from')}
                    thClassName="px-3 py-2 w-[130px]"
                  />
                  <SortableHeader label="Used it" sorted={sortedFor('used')} onToggle={() => toggleSort('used')} align="right" thClassName="px-3 py-2 w-[90px]" />
                  <SortableHeader label="Have access" sorted={sortedFor('access')} onToggle={() => toggleSort('access')} align="right" thClassName="px-3 py-2 w-[110px]" />
                  <SortableHeader label="Adoption" sorted={sortedFor('pct')} onToggle={() => toggleSort('pct')} thClassName="px-3 py-2 w-[140px]" />
                  <SortableHeader
                    label="Last used"
                    sorted={sortedFor('last_used_at')}
                    onToggle={() => toggleSort('last_used_at')}
                    thClassName="px-3 py-2 w-[150px]"
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-amp-line dark:divide-amp-dark-line">
                {toolsQ.isLoading ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-6 text-amp-ink-muted dark:text-amp-dark-ink-muted">
                      Loading adoption...
                    </td>
                  </tr>
                ) : toolsQ.error ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-6 text-red-700 dark:text-red-300">
                      Could not load: {toolsQ.error.message}
                    </td>
                  </tr>
                ) : sortedTools.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-6 text-amp-ink-muted dark:text-amp-dark-ink-muted">
                      No tools match the current filters.
                    </td>
                  </tr>
                ) : (
                  sortedTools.map((tool) => {
                    const awaiting = isAwaitingLaunch(tool);
                    const noData = tool.used === null || tool.used === undefined;
                    const neverUsed = !awaiting && !noData && tool.used === 0;
                    return (
                      <tr key={tool.tool_key} className={awaiting ? 'opacity-60' : ''}>
                        <td className="px-3 py-2 font-medium text-amp-ink dark:text-amp-dark-ink">{tool.label}</td>
                        <td className="px-3 py-2 text-amp-ink-muted dark:text-amp-dark-ink-muted">{tool.section ?? '--'}</td>
                        <td className="px-3 py-2">{surfaceLabel(tool.surface)}</td>
                        <td className="px-3 py-2">{tool.expected_from ? formatDate(tool.expected_from) : '--'}</td>
                        <td className="px-3 py-2 text-right">
                          {!awaiting && !noData && tool.used! > 0 ? (
                            <button
                              type="button"
                              onClick={() => setDrill({ toolKey: tool.tool_key, toolLabel: tool.label, principalRef: null })}
                              className="text-amp-ink underline hover:text-amp-olive-deep dark:text-amp-dark-ink dark:hover:text-amp-olive-dark"
                            >
                              {tool.used}
                            </button>
                          ) : (
                            <span>{tool.used ?? '--'}</span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right">{tool.access ?? '--'}</td>
                        <td className="px-3 py-2">
                          {awaiting ? (
                            <Pill tone={TONE_PILL_CLASS.warn}>Awaiting launch</Pill>
                          ) : noData ? (
                            <Pill tone={TONE_PILL_CLASS.warn}>No access to data</Pill>
                          ) : neverUsed ? (
                            <Pill tone={TONE_PILL_CLASS.bad}>Never used</Pill>
                          ) : (
                            <ProgressCell pct={tool.pct} />
                          )}
                        </td>
                        <td className="px-3 py-2">{formatDateMaybeTime(tool.last_used_at)}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </DataGridShell>
        </>
      )}

      {drill && (
        <DrillDownModal
          drill={drill}
          days={days}
          includeIt={includeIt}
          onSelectUser={(ref) => setDrill((prev) => (prev ? { ...prev, principalRef: ref } : prev))}
          onBackToList={() => setDrill((prev) => (prev ? { ...prev, principalRef: null } : prev))}
          onClose={() => setDrill(null)}
        />
      )}
      {settingsOpen && <SettingsModal onClose={() => setSettingsOpen(false)} />}
    </div>
  );
}

export function Adoption() {
  return (
    <ErrorBoundary>
      <AdoptionInner />
    </ErrorBoundary>
  );
}
